import { Component, PropsWithChildren, ReactNode } from 'react';
import { Navigate, Params } from 'react-router-dom';
import { PermissionsService } from '../../auth/services/PermissionsService';
import { BreadcrumbCachingService } from '../../breadcrumbs/services/BreadcrumbCachingService';
import { BreadcrumbFactory } from '../../breadcrumbs/utils/BreadcrumbFactory';
import { PATHS } from '../../routing/routes';
import { PageLoader } from '../components/PageLoader';
import { AuthenticationError, AuthorizationError, ErrorMessages, InvalidUrlError, getErrorMessage } from '../errors/errors';
import { UrlHelper } from '../utils/UrlHelper';
import { bind } from '../utils/decorators';
import { ErrorNotification, Notification } from '../utils/notifications/Notification';
import { NotificationManager } from '../utils/notifications/NotificationManager';

export type BaseProps = PropsWithChildren<object>;

export type BaseState = {};

type NavigateRequestType = { to: string; replace: boolean } | null;

export class BaseController<P extends BaseProps, S extends BaseState> extends Component<P, S> {
  protected readonly breadcrumbFactory = new BreadcrumbFactory();
  protected readonly breadcrumbCachingService = new BreadcrumbCachingService();
  protected readonly permissionsService = new PermissionsService();

  private canMount: boolean;

  private navigateRequest: NavigateRequestType = null;

  constructor(props: P, context: unknown) {
    super(props, context);

    this.state = this.getBaseState() as S;
    this.canMount = !this.shouldCheckPermissionToAccess();
  }

  protected getBaseState(): BaseState {
    return {};
  }

  /**
   * Called immediately after a component is mounted.
   * Controllers that extend BaseController should always call
   * super.componentDidMount() before performing their own operations
   */
  componentDidMount() {
    if (this.shouldCheckPermissionToAccess()) {
      this.checkPermissionToAccess()
        .then(() => {
          this.canMount = true;
          this.forceUpdate();
        })
        .catch((e) => this.handleError(e));
    }
  }

  /**
   * Called immediately after updating occurs. Not called for the initial render.
   * Controllers that extend BaseController should always call
   * super.componentDidUpdate() before performing their own operations
   */
  componentDidUpdate(_prevProps: Readonly<P>, _prevState: Readonly<S>) {}

  /**
   * Called immediately before a component is destroyed.
   * Controllers that extend BaseController should always call
   * super.componentWillUnmount() before performing their own operations
   */
  componentWillUnmount() {
    this.permissionsService.cancelAllRequests();
  }

  /**
   * Indicates whether the controller should delay rendering until the authorization policy
   * enforced by `checkPermissionToAccess` is validated.
   */
  protected shouldCheckPermissionToAccess(): boolean {
    return false;
  }

  /**
   * Check access for the current page. If access is not granted, throw AuthorizationError.
   * Controllers that provide a custom authorization policy must also implement the
   * `shouldCheckPermissionToAccess` method above, and have it return `true`.
   * These controllers are also required to *not* fetch data until the permission is checked.
   *
   * example:
   *  ```
   *  componentDidMount() {
   *    // required actions except data fetching
   *  }
   *
   *  protected checkPermissionToAccess(): Promise<void> {
   *    const allowed = await canIViewThisPage();
   *    if (!allowed) {
   *      throw new AuthorizationError(NEXT_PATH);
   *    }
   *    loadMyData();
   *  }
   *  ```
   *
   * @throws `AuthorizationError`
   */
  protected async checkPermissionToAccess(): Promise<void> {
    return;
  }

  protected showMessage(notification: Notification, defer = false) {
    NotificationManager.publish([notification], { defer });
  }

  @bind
  protected showMessages(notifications: Notification[], defer = false) {
    NotificationManager.publish(notifications, { defer });
  }

  @bind
  protected handleError(e: unknown, messages?: ErrorMessages, isSilentHandling: boolean = false) {
    const errorMessage = e instanceof Error ? e.message || e.name : 'UNKNOWN ERROR';
    console.warn(`An error occurred: ${errorMessage}`);

    // Map the error to a friendly message
    const message = getErrorMessage(e, messages);

    // Never silence authentication or authorization errors
    const silent = isSilentHandling && !(e instanceof AuthenticationError) && !(e instanceof AuthorizationError);

    // Store error redirection, if any
    let redirectTo: string | null = null;

    if (e instanceof InvalidUrlError) {
      if (!UrlHelper.isCurrentPath(PATHS.ORGANIZATION_DASHBOARD)) {
        redirectTo = PATHS.ORGANIZATION_DASHBOARD;
      }
    }
    if (e instanceof AuthenticationError) {
      redirectTo = UrlHelper.generateLogoutUrl();
    }
    if (e instanceof AuthorizationError) {
      redirectTo = e.redirectTo;
    }

    if (!silent) {
      // Only defer if the user is being logged out (the page will refresh)
      const deferredDisplay = UrlHelper.isLogoutUrl(redirectTo);
      this.showMessage(new ErrorNotification(message), deferredDisplay);
    }
    if (redirectTo) {
      this.redirect(redirectTo);
    }
  }

  /**
   * Takes a set of ID names expected in a route path and converts them to valid numbers.
   * If an ID does not exist in the path or it is not a valid number,
   * raises a `InvalidUrlError` that will be handled as a 404.
   * Controllers that need to use this method must be wrapped in `withRouter` so that `params`
   * is available in their props.
   * Example usage:
   *    - path: /path/to/:id1/and/:id2
   *      const { id1, id2 } = this.parseNumericIds(['id1', 'id2']));
   * @param keys the names of the IDs as they appear in the route path
   */
  protected parseNumericIds(keys: string[]): Record<string, number> {
    const ids: Record<string, number> = {};
    const params = 'params' in this.props && this.props['params'] ? (this.props['params'] as Params<string>) : {};
    for (const key of keys) {
      const id = Number.parseId(params[key]);
      if (id === null) {
        this.handleError(new InvalidUrlError());
      } else {
        ids[key] = id;
      }
    }
    return ids;
  }

  protected navigate(to: string) {
    this.navigateRequest = { to, replace: false };
    this.forceUpdate();
  }

  protected redirect(to: string) {
    this.navigateRequest = { to, replace: true };
    this.forceUpdate();
  }

  renderBase(children: ReactNode) {
    if (this.navigateRequest) {
      const { to, replace } = this.navigateRequest;

      // Reset the navigation request so that the re-render caused by a `Navigate`
      // to a controller that extends `BaseController` but does not re-mount
      // on pathname change does not get stuck (e.g. sub-controllers of a tabbed controller)
      this.navigateRequest = null;

      return <Navigate to={to} replace={replace} />;
    }
    if (!this.canMount) {
      return <PageLoader />;
    }
    return children;
  }

  render() {
    return this.renderBase(this.props.children);
  }
}
